import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiCamera, FiX, FiMenu, FiThumbsUp, FiThumbsDown, FiEye, FiEyeOff, FiMail, FiHeart, FiHome } from 'react-icons/fi';
import { FaInstagram, FaFacebook, FaWhatsapp, FaGoogle } from 'react-icons/fa';
import { signInWithPopup, signInWithRedirect, GoogleAuthProvider, getRedirectResult, onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db, storage } from './firebase';
import { collection, addDoc, query, where, getDocs, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import livingRoomImage from './assets/living-room.jpeg';
import mainScImage from './assets/main-sc.png';
import FeaturesDisplay from './FeaturesDisplay';
import PaintCanvas from './PaintCanvas';
import { 
  generateInterior, 
  generateExterior, 
  generateStyleTransfer,
  generatePaint,
  generateFloor,
  generateReplace
} from './api';
import { useSubscription, canGenerate, canSave } from './hooks/useSubscription';
import SubscriptionPaywall from './components/SubscriptionPaywall';
import { initRevenueCat, logInUser, logOutUser } from './utils/revenuecat';
import {
  trackFeatureTryIt,
  trackFeatureFormOpened,
  trackGenerationStarted,
  trackGenerationCompleted,
  trackGenerationFailed,
  trackGenerationRegenerated,
  trackImageSaved,
  trackSaveAttemptedUnsigned,
  trackSignInModalOpened,
  trackSignInInitiated,
  trackSignInCompleted,
  trackSignInFailed,
  trackSignOutClicked,
  trackModalOpened,
  trackModalClosed,
  trackSubscriptionModalShown,
  trackCameraOpened,
  trackPhotoCaptured,
  trackImageUploaded,
  trackExampleImageSelected,
  trackStyleSelected,
  trackColorPaletteSelected,
  trackShareClicked,
  trackProductLinkClicked,
  trackProductPinClicked,
  trackMyDesignsClicked,
  identifyUser,
  resetUser
} from './utils/analytics';
import './Landing.css';

const transformRoomImage = 'https://images.unsplash.com/photo-1560185007-cde436f6a4d0?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTh8fGxpdmluZyUyMHJvb218ZW58MHx8MHx8fDA%3D';

// Accepted image formats
const ACCEPTED_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
const ACCEPTED_FORMATS_TEXT = 'JPG, PNG, WEBP, GIF';

// Generating phrases for animation
const generatingPhrases = [
  'Preparing your room...',
  'Analyzing the space...',
  'Processing room layout...',
  'Scanning room dimensions...',
  'Identifying design elements...',
  'Evaluating color palette...',
  'Assessing lighting conditions...',
  'Mapping furniture placement...',
  'Integrating the product...',
  'Matching room aesthetics...',
  'Aligning product style...',
  'Calculating optimal placement...',
  'Positioning product...',
  'Applying realistic lighting...',
  'Simulating natural shadows...',
  'Adding depth and dimension...',
  'Adjusting perspective...',
  'Refining scale and proportions...',
  'Enhancing textures...',
  'Blending surfaces...',
  'Optimizing composition...',
  'Balancing visual elements...',
  'Refining color matching...',
  'Perfecting shadows...',
  'Enhancing details...',
  'Adding realistic reflections...',
  'Fine-tuning lighting angles...',
  'Adjusting material properties...',
  'Polishing edges...',
  'Harmonizing colors...',
  'Creating seamless integration...',
  'Applying photorealistic effects...',
  'Optimizing image quality...',
  'Rendering final details...',
  'Finalizing visualization...',
  'Applying final touches...',
  'Quality checking...',
  'Almost done...',
  'Just a moment more...',
  'Finalizing your image...',
];

// Unsplash images for the rotating collage - using reliable sources
const outerCircleImages = [
  'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=300&h=300&fit=crop&q=80',
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=300&h=300&fit=crop&q=80',
  'https://images.unsplash.com/photo-1493663284031-b7bd3c60a19f?w=300&h=300&fit=crop&q=80',
  'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=300&h=300&fit=crop&q=80',
  'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=300&h=300&fit=crop&q=80',
  'https://images.unsplash.com/photo-1536376072261-38c75010e6c9?w=300&h=300&fit=crop&q=80',
  'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=300&h=300&fit=crop&q=80',
  'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?w=300&h=300&fit=crop&q=80',
  'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?w=300&h=300&fit=crop&q=80',
  'https://images.unsplash.com/photo-1554995207-c18c203602cb?w=300&h=300&fit=crop&q=80',
];

const innerCircleImages = [
  'https://images.unsplash.com/photo-1506439773649-6e0eb8cfb237?w=200&h=200&fit=crop&q=80',
  'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=200&h=200&fit=crop&q=80',
  'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=200&h=200&fit=crop&q=80',
  'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=200&h=200&fit=crop&q=80',
  'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=200&h=200&fit=crop&q=80',
  'https://images.unsplash.com/photo-1506439773649-6e0eb8cfb237?w=200&h=200&fit=crop&q=80',
  'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=200&h=200&fit=crop&q=80',
  'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=200&h=200&fit=crop&q=80',
];

// Product type examples for typewriter effect
const productTypeExamples = [
  'office chair',
  'sofa',
  'desk',
  'lamp',
  'plant',
];

// Typewriter component for placeholder
function TypewriterPlaceholder({ examples }) {
  const [currentText, setCurrentText] = useState('');
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentExample = examples[currentExampleIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (currentText.length < currentExample.length) {
          setCurrentText(currentExample.substring(0, currentText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (currentText.length > 0) {
          setCurrentText(currentText.substring(0, currentText.length - 1));
        } else {
          setIsDeleting(false);
          setCurrentExampleIndex((prev) => (prev + 1) % examples.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentExampleIndex, examples]);

  return (
    <span>
      Product type (optional: e.g.,{' '}
      <span className="typewriter-text">{currentText}</span>
      <span className="typewriter-cursor">|</span>)
    </span>
  );
}

// Rotating phrase component for loading animation with progress bar
function GeneratingAnimation() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fadeClass, setFadeClass] = useState('fade-in');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Progress animation - simulate progress from 0 to 95%
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return 95; // Stop at 95% until generation completes
        return prev + Math.random() * 3 + 1; // Increment by 1-4% randomly
      });
    }, 200);

    // Text rotation
    const textInterval = setInterval(() => {
      setFadeClass('fade-out');
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % generatingPhrases.length);
        setFadeClass('fade-in');
      }, 300);
    }, 2500);

    return () => {
      clearInterval(progressInterval);
      clearInterval(textInterval);
    };
  }, []);

  return (
    <div className="generating-animation-container">
      <span className={`generating-text ${fadeClass}`}>
        {generatingPhrases[currentIndex]}
      </span>
      <div className="progress-bar-container">
        <div className="progress-bar" style={{ width: `${progress}%` }}></div>
      </div>
      <span className="progress-percentage">{Math.round(progress)}%</span>
    </div>
  );
}

// Room types
const ROOM_TYPES = [
  'kitchen', 'living room', 'home office', 'bathroom', 'dining', 
  'bedroom', 'study room', 'gaming room', 'office',
  'attic'
];

// Building types for exterior design
const BUILDING_TYPES = [
  'apartment',
  'house',
  'office_building',
  'residential',
  'retail',
  'villa'
];

// Building type images - using Unsplash search URLs
const BUILDING_TYPE_IMAGES = {
  'apartment': 'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8YXBhcnRtZW50JTIwYnVpbGRpbmd8ZW58MHx8MHx8fDA%3D',
  'house': 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8aG91c2UlMjBleHRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'office_building': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8b2ZmaWNlJTIwYnVpbGRpbmclMjBleHRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'residential': 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8cmVzaWRlbnRpYWwlMjBob3VzZXxlbnwwfHwwfHx8MA%3D%3D',
  'retail': 'https://images.unsplash.com/photo-1556910096-6f5e72db6803?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8cmV0YWlsJTIwc3RvcmUlMjBmYWNhZGV8ZW58MHx8MHx8fDA%3D',
  'villa': 'https://images.unsplash.com/photo-1613977257363-707ba9348227?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8dmlsbGElMjBleHRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D'
};

// Floor materials for floor restyle
const FLOOR_MATERIALS = [
  'brick',
  'wood',
  'cobble',
  'tiles',
  'carpet',
  'concrete',
  'marble',
  'hardwood',
  'laminate',
  'vinyl',
  'custom'  // Note: 'custom' requires customFloorDescription
];

// Styles
const STYLES = [
  'custom', 'christmas', 'modern', 'tropical', 'minimalistic', 'bohemian',
  'rustic', 'vintage', 'baroque', 'mediterranean', 'cyberpunk', 'ancient egyptian',
  'soho style', 'luxury', 'gamer', 'cozy', 'technoland', 'coastal', 'japanese',
  'cottagecore', 'gothic', 'ski chalet', 'medieval', '80s style', 'cartoon', 'wood'
];

// Style images - using Unsplash search URLs for each style
const STYLE_IMAGES = {
  // Leave custom as-is
  'custom': 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200&h=150&fit=crop',
  'christmas': 'https://images.unsplash.com/photo-1706510301252-c118fc2eb552?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MjB8fGNocmlzdG1hcyUyMGRlY29yfGVufDB8fDB8fHww',
  'modern': 'https://images.unsplash.com/photo-1593696140826-c58b021acf8b?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bW9kZXJuJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'tropical': 'https://images.unsplash.com/photo-1760067538559-49b2bfb5f42a?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MjR8fHRyb3BpY2FsJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'minimalistic': 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8bWluaW1hbGlzdGljJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'bohemian': 'https://images.unsplash.com/photo-1622763846204-5d0bf5031e06?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fGJvaGVtaWFuJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'rustic': 'https://images.unsplash.com/photo-1729932989809-f473b1523a9b?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTV8fHJ1c3RpYyUyMGhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8MHx8fDA%3D',
  'vintage': 'https://images.unsplash.com/photo-1646242571098-43d06a80466b?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NTh8fHZpbnRhZ2UlMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww',
  'baroque': 'https://images.unsplash.com/photo-1694514291510-018e4ffdf245?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTZ8fGJhcm9xdWUlMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww',
  'mediterranean': 'https://images.unsplash.com/photo-1667054430237-2a2afe4cb863?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mzl8fG1lZGl0ZXJyYW5lYW4lMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww',
  'cyberpunk': 'https://images.unsplash.com/photo-1649977107006-b769969c9725?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8N3x8Y3liZXJwdW5rJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'ancient egyptian': 'https://images.unsplash.com/photo-1624697156736-e846a4444904?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTl8fGFuY2llbnQlMjBlZ3lwdGlhbiUyMGhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8MHx8fDA%3D',
  'soho style': 'https://images.unsplash.com/photo-1677568554453-ae5baf28da6c?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8c29obyUyMHN0eWxlJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'luxury': 'https://images.unsplash.com/photo-1613545325268-9265e1609167?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8bHV4dXJ5JTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'gamer': 'https://images.unsplash.com/photo-1609815697866-033945af9797?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MzF8fGdhbWVyJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'cozy': 'https://images.unsplash.com/photo-1505693070124-1cce01268b88?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8Y296eSUyMGhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8MHx8fDA%3D',
  'technoland': 'https://images.unsplash.com/photo-1659894748283-74e2ccf7ea22?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mzl8fGN5YmVycHVuayUyMGhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8MHx8fDA%3D',
  'coastal': 'https://plus.unsplash.com/premium_photo-1736194029337-2c866fe5982f?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NzZ8fGNvYXN0YWwlMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww',
  'japanese': 'https://images.unsplash.com/photo-1735964519674-440909118206?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8amFwYW5lc2UlMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww',
  'cottagecore': 'https://images.unsplash.com/photo-1604629761628-5640ee399e18?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MzZ8fGNvdHRhZ2Vjb3JlJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'gothic': 'https://images.unsplash.com/photo-1725270865205-a9a5b9d925d4?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NTl8fGdvdGhpYyUyMGhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8MHx8fDA%3D',
  'ski chalet': 'https://images.unsplash.com/photo-1676661647337-1036036e1d5e?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NTV8fHNraSUyMGNoYWxldCUyMGhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8MHx8fDA%3D',
  'medieval': 'https://images.unsplash.com/photo-1739999064283-19ea78ab10e1?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTV8fG1lZGlldmFsJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  '80s style': 'https://images.unsplash.com/photo-1765734482970-da3c114d06c7?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8ODBzJTIwc3R5bGUlMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww',
  'cartoon': 'https://images.unsplash.com/photo-1612355524127-d361afbfc818?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8YW5pbWF0ZWQlMjBjYXJ0b29uJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHwwfHx8MA%3D%3D',
  'wood': 'https://images.unsplash.com/photo-1704265587121-37452a68ab05?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mzl8fHdvb2QlMjBob3VzZSUyMGludGVyaW9yfGVufDB8fDB8fHww'
};

// Color palettes with color representations
const COLOR_PALETTES = {
  'random': ['#FF6B9D', '#4ECDC4', '#FFE66D', '#95E1D3', '#F38181'],
  'millennial gray': ['#E8E8E8', '#D3D3D3', '#C0C0C0', '#A9A9A9', '#808080'],
  'terracotta mirage': ['#E07A5F', '#F2CC8F', '#E9C46A', '#D4A373', '#BC6C25'],
  'neon sunset': ['#FF6B9D', '#FF8C42', '#FFB347', '#FF6B35', '#FF8C69'],
  'forest hues': ['#2D5016', '#3D6B2A', '#5A8F3E', '#7FB069', '#95C085'],
  'peach orchard': ['#FFE5B4', '#FFCCCB', '#FFB6C1', '#FFA07A', '#FF8C69'],
  'fuchsia blossom': ['#FF1493', '#FF69B4', '#FFB6C1', '#FFC0CB', '#FFB6C1'],
  'emerald gem': ['#50C878', '#2E8B57', '#3CB371', '#66CDAA', '#7FFFD4'],
  'pastel breeze': ['#FFB6C1', '#B0E0E6', '#FFE4E1', '#E0E6FF', '#FFF0F5'],
  'azure mirage': ['#007FFF', '#1E90FF', '#87CEEB', '#B0E0E6', '#E0F6FF'],
  'twilight blue': ['#191970', '#000080', '#4169E1', '#6495ED', '#87CEEB'],
  'earth harmony': ['#8B4513', '#A0522D', '#CD853F', '#DEB887', '#F4A460'],
  'arctic lavender': ['#E6E6FA', '#DDA0DD', '#DA70D6', '#BA55D3', '#9370DB'],
  'antique sage': ['#9CAF88', '#87A96B', '#6B8E23', '#8FBC8F', '#98D8C8'],
  'earthy hues': ['#8B4513', '#A0522D', '#CD853F', '#D2691E', '#F4A460'],
  'velvet dusk': ['#4B0082', '#6A0DAD', '#8B008B', '#9370DB', '#BA55D3'],
  'ocean mist': ['#20B2AA', '#48D1CC', '#00CED1', '#5F9EA0', '#87CEEB'],
  'army dream': ['#556B2F', '#6B8E23', '#808000', '#9ACD32', '#ADFF2F'],
  'sakura bloom': ['#FFB6C1', '#FFC0CB', '#FFDAB9', '#FFE4E1', '#FFF0F5'],
  'lilac love': ['#DDA0DD', '#DA70D6', '#BA55D3', '#9370DB', '#8B7D9B'],
  'whimsical wish': ['#FF69B4', '#FF1493', '#FFB6C1', '#FFC0CB', '#FFE4E1'],
  'turquoise lagoon': ['#40E0D0', '#00CED1', '#48D1CC', '#5F9EA0', '#7FFFD4']
};

function Landing({ designId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [roomImage, setRoomImage] = useState(null);
  const [selectedExampleImage, setSelectedExampleImage] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState('');
  const [roomType, setRoomType] = useState('');
  const [style, setStyle] = useState('');
  const [colorPalette, setColorPalette] = useState('');
  const [customStylePrompt, setCustomStylePrompt] = useState('');
  const [exampleImages, setExampleImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [productLinks, setProductLinks] = useState([]);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState(null);
  const [showGoogleSignInModal, setShowGoogleSignInModal] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedImageDocId, setSavedImageDocId] = useState(null); // Firestore document ID for saved image
  const [savedImageUrl, setSavedImageUrl] = useState(null); // Firebase Storage URL
  const [showUnsavedConfirmModal, setShowUnsavedConfirmModal] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentDesignId, setCurrentDesignId] = useState(designId || null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [showPaintCanvas, setShowPaintCanvas] = useState(false);
  const [paintCanvasMode, setPaintCanvasMode] = useState('floor'); // 'floor' or 'paint'
  const [paintCanvasImage, setPaintCanvasImage] = useState(null);
  
  // Feature-specific state
  const [buildingType, setBuildingType] = useState('');
  const [floorMaterial, setFloorMaterial] = useState('');
  const [customFloorDescription, setCustomFloorDescription] = useState('');
  const [floorColor, setFloorColor] = useState('');
  const [floorMask, setFloorMask] = useState(null);
  const [paintColor, setPaintColor] = useState('#FF5733');
  const [paintMask, setPaintMask] = useState(null);
  const [objectMask, setObjectMask] = useState(null);
  const [styleReferenceImage, setStyleReferenceImage] = useState(null);
  const [styleReferenceUrl, setStyleReferenceUrl] = useState('');
  const [replacementObjectImage, setReplacementObjectImage] = useState(null);
  const [replacementObjectUrl, setReplacementObjectUrl] = useState('');
  const [replacementObjectDescription, setReplacementObjectDescription] = useState('');

  // Handle feature click - opens appropriate feature modal
  const handleFeatureClick = (featureId) => {
    console.log('Feature clicked:', featureId);
    // Map 'references' to 'style' for backward compatibility
    const mappedFeatureId = featureId === 'references' ? 'style' : featureId;
    console.log('Setting selected feature to:', mappedFeatureId);
    
    // Track feature form opened
    const featureTitles = {
      'interior': 'Interior Design',
      'exterior': 'Exterior Design',
      'style': 'Reference Style',
      'paint': 'Paint',
      'floor': 'Floor Restyle',
      'replace': 'Replace Object'
    };
    trackFeatureFormOpened(mappedFeatureId, featureTitles[mappedFeatureId] || mappedFeatureId, 'try_it_button');
    
    setSelectedFeature(mappedFeatureId);
    setMenuOpen(false); // Close side menu if open
    // Scroll to form after a brief delay to ensure state update
    setTimeout(() => {
      const formElement = document.querySelector('.generator-form');
      if (formElement) {
        console.log('Scrolling to form');
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        console.log('Form not found, scrolling to top');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 150);
  };

  // Get subscription status
  const subscription = useSubscription(user);

  // Get generation count from sessionStorage (for backward compatibility with non-authenticated users)
  const getGenerationCount = () => {
    // If user is Pro, they have unlimited (return 0 means unlimited)
    if (subscription.isPro) return 0;
    // For authenticated free users, check subscription data
    if (user && subscription.generationsRemaining !== null && subscription.generationsRemaining !== undefined) {
      return subscription.generationsRemaining;
    }
    // For non-authenticated users, check sessionStorage
    const count = sessionStorage.getItem('generationCount');
    return count ? parseInt(count, 10) : 0;
  };

  // Increment generation count for non-authenticated users only
  // (Authenticated users are tracked on backend via session ID)
  const incrementGenerationCount = () => {
    if (user) return; // Don't track in sessionStorage for authenticated users (backend handles it)
    if (subscription.isPro) return; // Pro users don't need tracking
    const currentCount = getGenerationCount();
    sessionStorage.setItem('generationCount', String(currentCount + 1));
  };

  // Get reason why submit is disabled (for tooltip/help text)
  const getDisabledReason = () => {
    if (!roomImage && !selectedExampleImage && !generatedImage) {
      return 'Please upload a room image or select an example image';
    }

    if (selectedFeature === 'interior') {
      if (!style) return 'Please select a style';
      if (!colorPalette) return 'Please select a color palette';
    }
    if (selectedFeature === 'exterior') {
      if (!buildingType) return 'Please select a building type';
      if (!style) return 'Please select a style';
      if (!colorPalette) return 'Please select a color palette';
    }
    if (selectedFeature === 'style') {
      if (!styleReferenceImage && !styleReferenceUrl) return 'Please upload or provide URL of style reference image';
    }
    if (selectedFeature === 'paint') {
      if (!paintMask) return 'Please upload a paint mask';
      if (!paintColor) return 'Please select a paint color';
    }
    if (selectedFeature === 'floor') {
      if (!floorMask) return 'Please upload a floor mask image';
      if (!floorMaterial) return 'Please select a floor material';
      if (floorMaterial === 'custom' && !customFloorDescription) return 'Please provide a custom floor description';
    }
    if (selectedFeature === 'replace') {
      if (!objectMask) return 'Please upload an object mask';
      if (!replacementObjectImage && !replacementObjectUrl && !replacementObjectDescription) {
        return 'Please provide replacement object (image, URL, or description)';
      }
    }

    if (!selectedFeature && !productImageUrl.trim()) {
      return 'Please enter a product image URL';
    }

    return '';
  };

  // Determine if submit button should be disabled based on selected feature
  const getSubmitDisabled = () => {
    // Basic validation: must have room image or example image (unless using base_image_url for chaining)
    if (!roomImage && !selectedExampleImage && !generatedImage) {
      return true;
    }

    // Feature-specific validation
    if (selectedFeature === 'interior') {
      return !style || !colorPalette;
    }
    if (selectedFeature === 'exterior') {
      return !buildingType || !style || !colorPalette;
    }
    if (selectedFeature === 'style') {
      return !styleReferenceImage && !styleReferenceUrl;
    }
    if (selectedFeature === 'paint') {
      return !paintMask || !paintColor;
    }
    if (selectedFeature === 'floor') {
      // Floor mask is required, floor material is required, and custom description if material is custom
      const missingMask = !floorMask;
      const missingMaterial = !floorMaterial;
      const missingCustomDesc = floorMaterial === 'custom' && !customFloorDescription;
      return missingMask || missingMaterial || missingCustomDesc;
    }
    if (selectedFeature === 'replace') {
      return !objectMask || (!replacementObjectImage && !replacementObjectUrl && !replacementObjectDescription);
    }

    // Old product placement feature
    if (!selectedFeature) {
      return !productImageUrl.trim();
    }

    return false;
  };
  // Product pins feature (currently disabled in UI)
  const [showProductPins, setShowProductPins] = useState(false);
  const [activeProductIndex, setActiveProductIndex] = useState(null);
  const [referenceSizeValue, setReferenceSizeValue] = useState('');
  const [referenceSizeUnit, setReferenceSizeUnit] = useState('in');
  const [hoveredMenuItem, setHoveredMenuItem] = useState(null);
  const [showBeforeAfter, setShowBeforeAfter] = useState({
    interior: true,
    exterior: true,
    style: true,
    paint: true,
    floor: true,
    replace: true
  });
  const [showAllStyles, setShowAllStyles] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const dropZoneRef = useRef(null);
  const colorPaletteGridRef = useRef(null);

  // Initialize Firebase Auth listener and RevenueCat
  useEffect(() => {
    // Initialize RevenueCat SDK
    initRevenueCat();
    
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
      
      // Clear generation count when user signs in
      if (currentUser) {
        sessionStorage.removeItem('generationCount');
        // Identify user in PostHog
        identifyUser(currentUser.uid, {
          email: currentUser.email,
          display_name: currentUser.displayName,
        });
        // Log in user to RevenueCat (link Firebase UID)
        await logInUser(currentUser.uid);
      } else {
        // Log out from RevenueCat when user signs out
        await logOutUser();
      }
      
      // Refresh subscription status when auth state changes
      if (subscription?.refresh) {
        subscription.refresh();
      }
    });

    // Check for redirect result on page load
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          console.log('Successfully signed in:', result.user);
          // Track sign-in completed (redirect method)
          trackSignInCompleted(result.user.uid, 'redirect');
          // Navigate to unique design ID route after redirect sign-in
          if (location.pathname === '/') {
            const newDesignId = generateDesignId();
            setCurrentDesignId(newDesignId);
            navigate(`/${newDesignId}`);
          }
        }
      })
      .catch((error) => {
        console.error('Error during redirect sign-in:', error);
        trackSignInFailed(error.code, error.message);
      });

    return () => unsubscribe();
  }, [navigate, location.pathname]);

  // Generate unique design ID
  const generateDesignId = () => {
    return `design-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Google Sign In Handler
  const handleGoogleSignIn = async () => {
    trackSignInInitiated('sign_in_button');
    const provider = new GoogleAuthProvider();
    try {
      // Try popup first
      const result = await signInWithPopup(auth, provider);
      console.log('Successfully signed in:', result.user);
      
      // Track successful sign-in
      trackSignInCompleted(result.user.uid, 'popup');
      
      setShowGoogleSignInModal(false);
      
      // Navigate to unique design ID route if not already there
      if (location.pathname === '/') {
        const newDesignId = generateDesignId();
        setCurrentDesignId(newDesignId);
        navigate(`/${newDesignId}`);
      }
    } catch (error) {
      // If popup is blocked, fall back to redirect
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/cancelled-popup-request') {
        console.log('Popup blocked, using redirect...');
        trackSignInInitiated('sign_in_redirect_fallback');
        await signInWithRedirect(auth, provider);
      } else {
        console.error('Error signing in:', error);
        trackSignInFailed(error.code, error.message);
        alert('Failed to sign in. Please try again.');
      }
    }
  };

  // Sign Out Handler
  const handleSignOut = async () => {
    trackSignOutClicked('header');
    try {
      resetUser(); // Reset PostHog user identification
      await signOut(auth);
      console.log('Successfully signed out');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Save image to Firebase Storage and Firestore
  const saveImageToFirebase = async (imageDataUrl, userId, designId) => {
    try {
      // Check storage limit before saving
      if (!canSave(subscription)) {
        setShowSubscriptionModal(true);
        throw new Error('Storage limit reached. Upgrade to Pro for unlimited storage.');
      }
      
      // Convert base64 to blob and upload to Storage
      const imageRef = ref(storage, `generated-images/${userId}/${Date.now()}.png`);
      await uploadString(imageRef, imageDataUrl, 'data_url');

      // Get download URL
      const downloadURL = await getDownloadURL(imageRef);

      // Generate design ID if not provided
      const finalDesignId = designId || `design-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Save metadata to Firestore
      const docRef = await addDoc(collection(db, 'generatedImages'), {
        userId: userId,
        designId: finalDesignId,
        imageUrl: downloadURL,
        createdAt: new Date(),
      });

      // Refresh subscription status to update design count
      if (subscription?.refresh) {
        subscription.refresh();
      }

      return { docId: docRef.id, downloadURL, designId: finalDesignId };
    } catch (error) {
      console.error('Error saving image to Firebase:', error);
      throw error;
    }
  };

  // Delete image from Firebase (for unsave)
  const deleteImageFromFirebase = async (docId) => {
    try {
      await deleteDoc(doc(db, 'generatedImages', docId));
      // Refresh subscription status
      if (subscription?.refresh) {
        subscription.refresh();
      }
    } catch (error) {
      console.error('Error deleting image from Firebase:', error);
      throw error;
    }
  };

  // Handle Save Click - Protected Action
  const handleSaveClick = async () => {
    if (!user) {
      // User not signed in, show sign-in modal
      setShowGoogleSignInModal(true);
      trackSaveAttemptedUnsigned('save_button');
      return;
    }

    if (!generatedImage) {
      return; // No image to save
    }

    try {
      if (isSaved && savedImageDocId) {
        // Unsave: Delete from Firebase
        await deleteImageFromFirebase(savedImageDocId);
        setIsSaved(false);
        setSavedImageDocId(null);
        setSavedImageUrl(null);
        trackImageSaved(false);
      } else {
        // Save: Upload to Firebase
        if (!canSave(subscription)) {
          setShowSubscriptionModal(true);
          return;
        }

        const result = await saveImageToFirebase(
          generatedImage,
          user.uid,
          currentDesignId
        );
        
        setIsSaved(true);
        setSavedImageDocId(result.docId);
        setSavedImageUrl(result.downloadURL);
        setCurrentDesignId(result.designId);
        trackImageSaved(true);
      }
    } catch (error) {
      console.error('Error saving/unsaving image:', error);
      setError(error.message || 'Failed to save image');
    }
  };

  // Validate file format
  const validateFile = (file) => {
    if (!file) return { valid: false, error: 'No file selected' };
    
    if (!ACCEPTED_FORMATS.includes(file.type)) {
      return { 
        valid: false, 
        error: `Invalid file format. Accepted formats: ${ACCEPTED_FORMATS_TEXT}` 
      };
    }
    
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return { 
        valid: false, 
        error: `File too large. Maximum size: 10MB. Your file: ${(file.size / 1024 / 1024).toFixed(2)}MB` 
      };
    }
    
    return { valid: true, error: null };
  };

  // Process file after validation
  const processFile = (file) => {
    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setError(null);
      setRoomImage(file);
    setSelectedExampleImage(null); // Clear example selection when uploading
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleRoomImageSelect(file);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleRoomImageSelect(file);
    }
  };

  // Camera capture handler
  const handleCameraClick = async () => {
    const featureType = selectedFeature || 'product_placement';
    trackCameraOpened(featureType, 'environment');
    try {
      // Request camera access
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Use rear camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setStream(mediaStream);
      setShowCamera(true);
      trackModalOpened('camera', 'camera_button');
    } catch (err) {
      setError('Unable to access camera. Please check permissions or use file upload instead.');
      console.error('Camera access error:', err);
    }
  };

  // Set video stream when camera opens
  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(err => {
        console.error('Error playing video:', err);
        setError('Error starting camera preview');
      });
    }
  }, [showCamera, stream]);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      context.drawImage(video, 0, 0);

      // Track photo captured
      const featureType = selectedFeature || 'product_placement';
      trackPhotoCaptured(featureType, {
        width: canvas.width,
        height: canvas.height
      });

      // Convert canvas to blob, then to file
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
          handleRoomImageSelect(file);
          closeCamera();
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const closeCamera = () => {
    trackModalClosed('camera', 'close_button');
    // Stop all tracks to release camera
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const handleSubmit = async (e, isRegenerate = false) => {
    if (e) {
      e.preventDefault();
    }
    setError(null);

    // Check generation limit - use subscription status
    const canGenerateNow = canGenerate(subscription);
    if (!canGenerateNow) {
      const generationCount = getGenerationCount();
      trackSubscriptionModalShown(generationCount, selectedFeature || 'product_placement');
      setShowSubscriptionModal(true);
      return;
    }

    // Track generation started
    const generationStartTime = Date.now();
    const featureType = selectedFeature || 'product_placement';
    
    if (isRegenerate) {
      const regenCount = parseInt(sessionStorage.getItem('regenerationCount') || '0', 10) + 1;
      sessionStorage.setItem('regenerationCount', String(regenCount));
      trackGenerationRegenerated(featureType, regenCount, !!user);
    } else {
      trackGenerationStarted(featureType, {
        user_signed_in: !!user,
        room_type: roomType || null,
        style: style || null,
        has_uploaded_image: !!roomImage,
        has_example_image: !!selectedExampleImage,
        has_product_url: !!productImageUrl?.trim(),
        generation_count: user ? null : getGenerationCount(),
      });
    }

    // Reset product overlay state on each submit/regenerate
    setShowProductPins(false);
    setActiveProductIndex(null);
    // Only clear generated image and links if not regenerating
    if (!isRegenerate) {
    setGeneratedImage(null);
      setProductLinks([]);
    } else {
      // Keep the image but show loading overlay
      setLoading(true);
    }

    // Validate: either roomImage OR selectedExampleImage must be provided (unless using base_image_url for chaining)
    if (!selectedFeature && !roomImage && !selectedExampleImage) {
      // Only require room image for old product placement feature
      if (!productImageUrl.trim()) {
        setError('Please upload a room image or select an example image');
        return;
      }
    }

    // Old product placement feature validation
    if (!selectedFeature) {
      if (!productImageUrl.trim()) {
        setError('Please enter a product image URL');
        return;
      }

      // Validate URL format
      try {
        new URL(productImageUrl.trim());
      } catch {
        setError('Please enter a valid product image URL (e.g., https://example.com/image.jpg)');
        return;
      }
    }

    // Validate custom style prompt if style is custom
    if (style === 'custom' && !customStylePrompt.trim()) {
      setError('Please enter a custom style description');
      return;
    }

    setLoading(true);

    try {
      let data;
      
      // Use new API if feature is selected, otherwise use old endpoint for backward compatibility
      if (selectedFeature) {
        // Route to appropriate feature API
        const params = {
          roomImage: roomImage || null,
          exampleImageUrl: selectedExampleImage || null,
          baseImageUrl: generatedImage || null, // For chaining operations
        };

        // Common params
        if (roomType) params.roomType = roomType;
        if (style) params.style = style;
        if (colorPalette) params.colorPalette = colorPalette;
        if (style === 'custom' && customStylePrompt) {
          params.customStylePrompt = customStylePrompt.trim();
        }

        // Feature-specific params
        switch (selectedFeature) {
          case 'interior':
            data = await generateInterior(params, user);
            break;
          case 'exterior':
            if (!buildingType) {
              throw new Error('Building type is required for exterior design');
            }
            params.buildingType = buildingType;
            data = await generateExterior(params, user);
            break;
          case 'style':
            if (!styleReferenceImage && !styleReferenceUrl) {
              throw new Error('Style reference image or URL is required');
            }
            params.styleReferenceImage = styleReferenceImage;
            params.styleReferenceUrl = styleReferenceUrl;
            data = await generateStyleTransfer(params, user);
            break;
          case 'paint':
            if (!paintMask) {
              throw new Error('Paint mask is required');
            }
            if (!paintColor) {
              throw new Error('Paint color is required');
            }
            params.paintMask = paintMask;
            params.paintColor = paintColor;
            data = await generatePaint(params, user);
            break;
          case 'floor':
            if (!floorMask) {
              throw new Error('Floor mask is required');
            }
            if (!floorMaterial) {
              throw new Error('Floor material is required');
            }
            if (floorMaterial === 'custom' && !customFloorDescription) {
              throw new Error('Custom floor description is required when material is custom');
            }
            params.floorMask = floorMask;
            params.floorMaterial = floorMaterial;
            params.customFloorDescription = customFloorDescription;
            params.floorColor = floorColor;
            data = await generateFloor(params, user);
            break;
          case 'replace':
            if (!objectMask) {
              throw new Error('Object mask is required');
            }
            if (!replacementObjectImage && !replacementObjectUrl && !replacementObjectDescription) {
              throw new Error('Replacement object (image, URL, or description) is required');
            }
            params.objectMask = objectMask;
            params.replacementObjectImage = replacementObjectImage;
            params.replacementObjectUrl = replacementObjectUrl;
            params.replacementObjectDescription = replacementObjectDescription;
            data = await generateReplace(params, user);
            break;
          default:
            throw new Error(`Unknown feature: ${selectedFeature}`);
        }
      } else {
        // Original endpoint for backward compatibility (product placement)
        const formData = new FormData();
        
        if (roomImage) {
          formData.append('room_image', roomImage);
        } else if (selectedExampleImage) {
          formData.append('example_image_url', selectedExampleImage);
        }
        
        formData.append('product_image_url', productImageUrl.trim());
        formData.append('product_type', 'product');
        
        if (roomType) formData.append('room_type', roomType);
        if (style) formData.append('style', style);
        if (colorPalette) formData.append('color_palette', colorPalette);
        if (style === 'custom' && customStylePrompt) {
          formData.append('custom_style_prompt', customStylePrompt.trim());
        }

        if (referenceSizeValue && !Number.isNaN(Number(referenceSizeValue))) {
          formData.append('reference_size_value', String(referenceSizeValue));
          formData.append('reference_size_unit', referenceSizeUnit);
        }

        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await fetch(`${apiUrl}/generate-room`, {
          method: 'POST',
          body: formData,
        });

        data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Something went wrong');
        }
      }

      console.log('Full API response:', data);

      if (data.image_b64) {
        const generationTime = Date.now() - generationStartTime;
        
        // Track generation completed
        trackGenerationCompleted(featureType, generationTime, !!user);
        
        const imageDataUrl = `data:image/png;base64,${data.image_b64}`;
        setGeneratedImage(imageDataUrl);
        
        // Auto-save to Firebase if user is signed in
        if (user && canSave(subscription)) {
          try {
            const result = await saveImageToFirebase(
              imageDataUrl,
              user.uid,
              currentDesignId
            );
            setIsSaved(true);
            setSavedImageDocId(result.docId);
            setSavedImageUrl(result.downloadURL);
            setCurrentDesignId(result.designId);
            trackImageSaved(true);
          } catch (error) {
            console.error('Auto-save failed:', error);
            // Don't show error to user, just log it - they can manually save later
          }
        } else {
          // Reset save state for new image
          setIsSaved(false);
          setSavedImageDocId(null);
          setSavedImageUrl(null);
        }
        
        // Increment generation count for non-authenticated users
        incrementGenerationCount();
        
        // Refresh subscription status to update generationsRemaining
        if (subscription?.refresh) {
          subscription.refresh();
        }
        
        // Store original image URL for comparison toggle
        if (imagePreview) {
          setOriginalImageUrl(imagePreview);
        } else if (selectedExampleImage) {
          setOriginalImageUrl(selectedExampleImage);
        }
        
        // Close feature modal after successful generation
        if (selectedFeature) {
          setShowFeatureModal(false);
        }
        
        // Product links are now loaded on-demand via /api/analyze-products endpoint
        // This prevents timeout issues (links loaded when user clicks "Show products")
        setProductLinks([]);
      } else {
        throw new Error('No image received');
      }
    } catch (err) {
      setError(err.message || 'Failed to generate image');
    } finally {
      setLoading(false);
    }
  };

  const performReset = () => {
    setRoomImage(null);
    setSelectedExampleImage(null);
    setProductImageUrl('');
    setRoomType('');
    setStyle('');
    setColorPalette('');
    setCustomStylePrompt('');
    setGeneratedImage(null);
    setProductLinks([]);
    setImagePreview(null);
    setError(null);
    setOriginalImageUrl(null);
    setShowOriginal(false);
    setShowFeedbackModal(false);
    setFeedbackText('');
    setIsSaved(false);
    setSavedImageDocId(null);
    setSavedImageUrl(null);
    closeCamera();
  };

  const handleReset = () => {
    // If there's a generated image that's not saved, show confirmation
    if (generatedImage && !isSaved) {
      setShowUnsavedConfirmModal(true);
    } else {
      performReset();
    }
  };

  const handleConfirmReset = () => {
    setShowUnsavedConfirmModal(false);
    performReset();
  };

  const handleCancelReset = () => {
    setShowUnsavedConfirmModal(false);
  };

  const handleThumbsUp = async () => {
    // Send positive feedback (you can add API call here)
    console.log('Positive feedback received');
    // You can add an API call to track positive feedback
    alert('Thank you for your feedback!');
  };

  const handleThumbsDown = () => {
    setShowFeedbackModal(true);
  };

  const handleFeedbackSubmit = async () => {
    // Send feedback (you can add API call here)
    console.log('Feedback submitted:', feedbackText);
    // You can add an API call to send feedback
    setShowFeedbackModal(false);
    setFeedbackText('');
    alert('Thank you for your feedback!');
  };

  const handleShare = (platform) => {
    const shareUrl = window.location.href;
    const shareText = 'Check out this AI-generated room design!';
    
    let shareLink = '';
    
    switch(platform) {
      case 'instagram':
        // Instagram doesn't support direct sharing via URL, so copy to clipboard
        navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard! Paste it in your Instagram post.');
        return;
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        break;
      case 'mail':
        shareLink = `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(shareUrl)}`;
        break;
      default:
        return;
    }
    
    if (shareLink) {
      window.open(shareLink, '_blank', 'width=600,height=400');
    }
  };

  const handleRoomImageSelect = (file) => {
    // If user selects a room image, clear example image selection
    setSelectedExampleImage(null);
    
    // Track image upload
    const featureType = selectedFeature || 'product_placement';
    const fileSizeKb = Math.round(file.size / 1024);
    trackImageUploaded('file_input', fileSizeKb, file.type, featureType);
    
    processFile(file);
  };

  const handleExampleImageSelect = (url) => {
    // If user selects an example image, clear uploaded image
    setRoomImage(null);
    setImagePreview(null);
    setSelectedExampleImage(url);
    
    // Track example image selection
    trackExampleImageSelected(url, roomType || null);
  };

  // Fetch example images from backend
  useEffect(() => {
    const fetchExampleImages = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await fetch(`${apiUrl}/example-images`);
        const data = await response.json();
        if (data.example_images) {
          setExampleImages(data.example_images);
        }
      } catch (err) {
        console.error('Error fetching example images:', err);
      }
    };
    fetchExampleImages();
  }, []);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  if (generatedImage) {
    console.log('Rendering result view'); // Debug log
    console.log('productLinks state:', productLinks); // Debug log
    console.log('productLinks type:', typeof productLinks); // Debug log
    console.log('productLinks is array?', Array.isArray(productLinks)); // Debug log
    console.log('productLinks length:', productLinks?.length); // Debug log
    
    return (
      <div className="landing-container">
        <div className="result-view">
          <div className="result-header">
            <h1 className="logo-small">AI Home Designer</h1>
            <div className="result-header-actions">
              <button 
                onClick={handleSaveClick}
                className={`save-btn ${isSaved ? 'saved' : ''}`}
                disabled={loading}
              >
                <FiHeart className={isSaved ? 'filled' : ''} />
                <span>Save</span>
              </button>
              {false && !(showFeatureModal || showGoogleSignInModal || showSubscriptionModal || showFeedbackModal || showPaintCanvas) && (
                <button
                  onClick={async () => {
                    if (!showProductPins && (!productLinks || productLinks.length === 0)) {
                      // Load product links on first click
                      setLoading(true);
                      try {
                        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                        const formData = new FormData();
                        
                        // Handle both base64 data URLs and regular image URLs
                        if (generatedImage.startsWith('data:')) {
                          // Extract base64 from data URL
                          const base64Data = generatedImage.split(',')[1] || generatedImage;
                          formData.append('image_b64', base64Data);
                        } else {
                          // Regular URL
                          formData.append('image_url', generatedImage);
                        }
                        
                        const response = await fetch(`${apiUrl}/api/analyze-products`, {
                          method: 'POST',
                          body: formData,
                        });
                        
                        const data = await response.json();
                        if (response.ok && data.product_links) {
                          setProductLinks(data.product_links);
                          setShowProductPins(true);
                        } else {
                          setError('Failed to load product links');
                        }
                      } catch (err) {
                        setError('Failed to load product links: ' + err.message);
                      } finally {
                        setLoading(false);
                      }
                    } else {
                      setShowProductPins(!showProductPins);
                    }
                  }}
                  className={`show-products-btn ${showProductPins ? 'active' : ''}`}
                  disabled={loading}
                >
                  {showProductPins ? 'Hide products' : 'Show products'}
                </button>
              )}
              <button 
                onClick={(e) => handleSubmit(e, true)} 
                className="regenerate-btn"
                disabled={loading}
              >
                {loading ? 'Regenerating...' : 'Regenerate'}
              </button>
              <button onClick={handleReset} className="close-btn">
                <FiX />
              </button>
            </div>
          </div>
          <div className="result-content-wrapper">
            <div className="result-image-wrapper">
              {loading && <div className="loading-overlay-result">Regenerating...</div>}
              
              {/* Toggle Icon */}
              {originalImageUrl && (
                <div 
                  className={`image-toggle-container ${showOriginal ? 'active' : ''}`}
                  onMouseEnter={() => {
                    // Only hover on desktop (non-touch devices)
                    if (window.matchMedia('(hover: hover)').matches) {
                      setShowOriginal(true);
                    }
                  }}
                  onMouseLeave={() => {
                    if (window.matchMedia('(hover: hover)').matches) {
                      setShowOriginal(false);
                    }
                  }}
                  onClick={() => {
                    // Toggle on mobile/touch devices
                    if (!window.matchMedia('(hover: hover)').matches) {
                      setShowOriginal(!showOriginal);
                    }
                  }}
                >
                  <div className="toggle-icon">
                    {showOriginal ? <FiEyeOff /> : <FiEye />}
                  </div>
                </div>
              )}
              
              {/* Image Comparison */}
              <div className="image-comparison-wrapper">
                <img 
                  src={generatedImage} 
                  alt="Generated room" 
                  className={`result-image ${showOriginal ? 'fade-out' : ''}`} 
                />
                {originalImageUrl && (
                  <img 
                    src={originalImageUrl} 
                    alt="Original room" 
                    className={`result-image original-image ${showOriginal ? 'fade-in' : ''}`} 
                  />
                )}
              </div>
              
              {/* Product Pins & Side Panel */}
              {showProductPins && productLinks && productLinks.length > 0 && (
                <>
                  <div className="product-pins-overlay">
                    {productLinks.map((product, index) => {
                      const position = product.metadata?.position;
                      if (!position || position.x === undefined || position.y === undefined) {
                        return null;
                      }
                      return (
                        <div
                          key={index}
                          className={`product-pin ${activeProductIndex === index ? 'active' : ''}`}
                          style={{
                            left: `${position.x * 100}%`,
                            top: `${position.y * 100}%`,
                          }}
                          onClick={() => {
                            const featureType = selectedFeature || 'product_placement';
                            trackProductPinClicked(index, { x: position.x, y: position.y });
                            setActiveProductIndex(activeProductIndex === index ? null : index);
                          }}
                        >
                          {index + 1}
                        </div>
                      );
                    })}
                  </div>
                  
                  {activeProductIndex !== null && productLinks[activeProductIndex] && (
                    <div className="product-links-side-panel">
                      <div className="side-panel-product-title">
                        {productLinks[activeProductIndex].product_name}
                      </div>
                      {productLinks[activeProductIndex].links && productLinks[activeProductIndex].links.length > 0 ? (
                        <div className="product-links-list">
                          {productLinks[activeProductIndex].links.map((link, linkIndex) => (
                            <a
                              key={linkIndex}
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="product-link-item"
                              onClick={() => {
                                const featureType = selectedFeature || 'product_placement';
                                const hostname = new URL(link).hostname.replace('www.', '');
                                const productSource = hostname.includes('amazon') ? 'amazon' 
                                  : hostname.includes('wayfair') ? 'wayfair'
                                  : hostname.includes('target') ? 'target'
                                  : 'other';
                                trackProductLinkClicked(activeProductIndex, productSource, featureType);
                              }}
                            >
                              {new URL(link).hostname.replace('www.', '')}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="no-links-message">No product links available</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Action Buttons - Below Image */}
            <div className="result-actions-container">
              {/* Mobile: Regenerate and Show Products row */}
              <div className="result-action-buttons-row">
                <button 
                  onClick={(e) => handleSubmit(e, true)} 
                  className="regenerate-btn mobile-action-btn"
                  disabled={loading}
                >
                  {loading ? 'Regenerating...' : 'Regenerate'}
                </button>
                {false && !(showFeatureModal || showGoogleSignInModal || showSubscriptionModal || showFeedbackModal || showPaintCanvas) && (
                  <button
                    onClick={async () => {
                      if (!showProductPins && (!productLinks || productLinks.length === 0)) {
                        // Load product links on first click
                        setLoading(true);
                        try {
                          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                          const formData = new FormData();
                          
                          // Handle both base64 data URLs and regular image URLs
                          if (generatedImage.startsWith('data:')) {
                            // Extract base64 from data URL
                            const base64Data = generatedImage.split(',')[1] || generatedImage;
                            formData.append('image_b64', base64Data);
                          } else {
                            // Regular URL
                            formData.append('image_url', generatedImage);
                          }
                          
                          const response = await fetch(`${apiUrl}/api/analyze-products`, {
                            method: 'POST',
                            body: formData,
                          });
                          
                          const data = await response.json();
                          if (response.ok && data.product_links) {
                            setProductLinks(data.product_links);
                            setShowProductPins(true);
                          } else {
                            setError('Failed to load product links');
                          }
                        } catch (err) {
                          setError('Failed to load product links: ' + err.message);
                        } finally {
                          setLoading(false);
                        }
                      } else {
                        setShowProductPins(!showProductPins);
                      }
                    }}
                    className={`show-products-btn mobile-action-btn ${showProductPins ? 'active' : ''}`}
                    disabled={loading}
                  >
                    {showProductPins ? 'Hide products' : 'Show products'}
                  </button>
                )}
              </div>

              {/* Feedback and Share Buttons */}
              <div className="result-actions-bottom">
                <div className="feedback-buttons">
                  <button 
                    className="action-btn thumbs-up-btn"
                    onClick={handleThumbsUp}
                    aria-label="Thumbs up"
                  >
                    <FiThumbsUp />
                  </button>
                  <button 
                    className="action-btn thumbs-down-btn"
                    onClick={handleThumbsDown}
                    aria-label="Thumbs down"
                  >
                    <FiThumbsDown />
                  </button>
                </div>
                
                <div className="share-buttons">
                  <button 
                    className="action-btn share-btn instagram"
                    onClick={() => handleShare('instagram')}
                    aria-label="Share on Instagram"
                  >
                    <FaInstagram />
                  </button>
                  <button 
                    className="action-btn share-btn facebook"
                    onClick={() => handleShare('facebook')}
                    aria-label="Share on Facebook"
                  >
                    <FaFacebook />
                  </button>
                  <button 
                    className="action-btn share-btn whatsapp"
                    onClick={() => handleShare('whatsapp')}
                    aria-label="Share on WhatsApp"
                  >
                    <FaWhatsapp />
                  </button>
                  <button 
                    className="action-btn share-btn mail"
                    onClick={() => handleShare('mail')}
                    aria-label="Share via Email"
                  >
                    <FiMail />
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Feedback Modal */}
          {showFeedbackModal && (
            <div className="feedback-modal-overlay" onClick={() => setShowFeedbackModal(false)}>
              <div className="feedback-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="feedback-modal-header">
                  <h3>We'd love your feedback</h3>
                  <button 
                    className="close-feedback-btn"
                    onClick={() => setShowFeedbackModal(false)}
                  >
                    <FiX />
                  </button>
                </div>
                <div className="feedback-modal-body">
                  <p>What can we improve?</p>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Tell us what went wrong or what you'd like to see improved..."
                    className="feedback-textarea"
                    rows="5"
                  />
                </div>
                <div className="feedback-modal-footer">
                  <button 
                    className="feedback-cancel-btn"
                    onClick={() => setShowFeedbackModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="feedback-submit-btn"
                    onClick={handleFeedbackSubmit}
                    disabled={!feedbackText.trim()}
                  >
                    Submit Feedback
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Loading Modal */}
          {loading && (
            <div className="loading-modal-overlay">
              <div className="loading-modal-content">
                <div className="loading-modal-spinner-container">
                  <div className="loading-modal-spinner"></div>
                </div>
                <div className="loading-modal-text">
                  <GeneratingAnimation />
                </div>
              </div>
            </div>
          )}
          
          {/* Product links are now shown via pins & side panel overlay */}
        </div>
      </div>
    );
  }

  return (
    <div className="landing-container">
      <div className="image-orbits">
        {/* Outer rotating circle */}
        <div className="outer-circle">
          {outerCircleImages.map((img, index) => (
            <div
              key={`outer-${index}`}
              className="orbit-item outer-item"
              style={{
                '--angle': `${(360 / outerCircleImages.length) * index}deg`,
                '--radius': '380px',
              }}
            >
              <img 
                src={img} 
                alt={`Room ${index + 1}`} 
                className="orbit-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          ))}
        </div>

        {/* Inner rotating circle (opposite direction) */}
        <div className="inner-circle">
          {innerCircleImages.map((img, index) => (
            <div
              key={`inner-${index}`}
              className="orbit-item inner-item"
              style={{
                '--angle': `${(360 / innerCircleImages.length) * index}deg`,
                '--radius': '240px',
              }}
            >
              <img 
                src={img} 
                alt={`Product ${index + 1}`} 
                className="orbit-image small"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="landing-content">
        {/* Hamburger Menu Button */}
        <button 
          className="hamburger-menu-btn"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
        >
          <FiMenu />
        </button>

        {/* My Designs Button - Top Right */}
        {user && (
          <button
            className="my-designs-btn"
            onClick={() => {
              // Generate consistent design ID based on user UID
              const userDesignId = `user-${user.uid}`;
              navigate(`/${userDesignId}`);
            }}
            aria-label="My Designs"
          >
            <FiHome />
            <span>My Designs</span>
          </button>
        )}

        {/* Side Panel Menu */}
        <div className={`side-panel ${menuOpen ? 'open' : ''}`}>
          <div className="side-panel-header">
            <h2>Features</h2>
            <button 
              className="close-menu-btn"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
            >
              <FiX />
            </button>
          </div>
          <div className="side-panel-content">
            {/* Sign In Prompt or User Info */}
            {!user ? (
              <div className="sign-in-prompt">
                <p className="sign-in-text">
                  Registered users can save designs and add items to their wishlist
                </p>
                <button className="sign-in-btn" onClick={handleGoogleSignIn}>
                  Sign In
                </button>
              </div>
            ) : (
              <div className="user-info">
                <div className="user-avatar">
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={user.displayName || 'User'} />
                  ) : (
                    <div className="user-avatar-placeholder">
                      {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                    </div>
                  )}
                </div>
                <div className="user-details">
                  <p className="user-name">{user.displayName || 'User'}</p>
                  <p className="user-email">{user.email}</p>
                </div>
                <button className="sign-out-btn" onClick={handleSignOut}>
                  Sign Out
                </button>
              </div>
            )}

            <div 
              className="menu-item"
              onMouseEnter={() => setHoveredMenuItem('interior')}
              onMouseLeave={() => setHoveredMenuItem(null)}
            >
              <h3>Interior Design</h3>
              <p>Transform your interior spaces with AI-powered design</p>
              {hoveredMenuItem === 'interior' && (
                <div className="menu-item-preview">
                  <div className="before-after-container">
                    <img 
                      src="https://iili.io/fMA6Zvt.png" 
                      alt="Before" 
                      className={`before-after-image ${showBeforeAfter.interior ? 'show' : 'hide'}`}
                    />
                    <img 
                      src="https://iili.io/fMA6Zvt.png" 
                      alt="After" 
                      className={`before-after-image ${!showBeforeAfter.interior ? 'show' : 'hide'}`}
                    />
                  </div>
                  <button 
                    className="try-it-btn"
                    onClick={() => {
                      if (!user) {
                        setShowGoogleSignInModal(true);
                      } else {
                        handleFeatureClick('interior');
                      }
                    }}
                  >
                    Try it
                  </button>
                </div>
              )}
            </div>
            <div 
              className="menu-item"
              onMouseEnter={() => setHoveredMenuItem('exterior')}
              onMouseLeave={() => setHoveredMenuItem(null)}
            >
              <h3>Exterior Design</h3>
              <p>Redesign outdoor spaces and exteriors</p>
              {hoveredMenuItem === 'exterior' && (
                <div className="menu-item-preview">
                  <div className="before-after-container">
                    <img 
                      src="https://iili.io/fMAi4gp.png" 
                      alt="Before" 
                      className={`before-after-image ${showBeforeAfter.exterior ? 'show' : 'hide'}`}
                    />
                    <img 
                      src="https://iili.io/fMAi4gp.png" 
                      alt="After" 
                      className={`before-after-image ${!showBeforeAfter.exterior ? 'show' : 'hide'}`}
                    />
                  </div>
                  <button 
                    className="try-it-btn"
                    onClick={() => {
                      if (!user) {
                        setShowGoogleSignInModal(true);
                      } else {
                        handleFeatureClick('exterior');
                      }
                    }}
                  >
                    Try it
                  </button>
                </div>
              )}
            </div>
            <div 
              className="menu-item"
              onMouseEnter={() => setHoveredMenuItem('references')}
              onMouseLeave={() => setHoveredMenuItem(null)}
            >
              <h3>Reference Style</h3>
              <p>Show AI what you like and let it style your space</p>
              {hoveredMenuItem === 'references' && (
                <div className="menu-item-preview">
                  <div className="before-after-container">
                    <img 
                      src="https://iili.io/fMA6Zvt.png" 
                      alt="Before" 
                      className={`before-after-image ${showBeforeAfter.style ? 'show' : 'hide'}`}
                    />
                    <img 
                      src="https://iili.io/fMA6Zvt.png" 
                      alt="After" 
                      className={`before-after-image ${!showBeforeAfter.style ? 'show' : 'hide'}`}
                    />
                  </div>
                  <button 
                    className="try-it-btn"
                    onClick={() => {
                      if (!user) {
                        setShowGoogleSignInModal(true);
                      } else {
                        handleFeatureClick('style');
                      }
                    }}
                  >
                    Try it
                  </button>
                </div>
              )}
            </div>
            <div 
              className="menu-item"
              onMouseEnter={() => setHoveredMenuItem('paint')}
              onMouseLeave={() => setHoveredMenuItem(null)}
            >
              <h3>Paint</h3>
              <p>Visualize paint colors and finishes</p>
              {hoveredMenuItem === 'paint' && (
                <div className="menu-item-preview">
                  <div className="before-after-container">
                    <img 
                      src="https://iili.io/fMAbQcJ.png" 
                      alt="Before" 
                      className={`before-after-image ${showBeforeAfter.paint ? 'show' : 'hide'}`}
                    />
                    <img 
                      src="https://iili.io/fMAbQcJ.png" 
                      alt="After" 
                      className={`before-after-image ${!showBeforeAfter.paint ? 'show' : 'hide'}`}
                    />
                  </div>
                  <button 
                    className="try-it-btn"
                    onClick={() => {
                      if (!user) {
                        setShowGoogleSignInModal(true);
                      } else {
                        handleFeatureClick('paint');
                      }
                    }}
                  >
                    Try it
                  </button>
                </div>
              )}
            </div>
            <div 
              className="menu-item"
              onMouseEnter={() => setHoveredMenuItem('floor')}
              onMouseLeave={() => setHoveredMenuItem(null)}
            >
              <h3>Floor Restyle</h3>
              <p>Redesign and restyle your floors</p>
              {hoveredMenuItem === 'floor' && (
                <div className="menu-item-preview">
                  <div className="before-after-container no-border">
                    <img 
                      src="https://iili.io/fMROkZB.png" 
                      alt="Before" 
                      className={`before-after-image ${showBeforeAfter.floor ? 'show' : 'hide'}`}
                    />
                    <img 
                      src="https://iili.io/fMROkZB.png" 
                      alt="After" 
                      className={`before-after-image ${!showBeforeAfter.floor ? 'show' : 'hide'}`}
                    />
                  </div>
                  <button 
                    className="try-it-btn"
                    onClick={() => {
                      if (!user) {
                        setShowGoogleSignInModal(true);
                      } else {
                        handleFeatureClick('floor');
                      }
                    }}
                  >
                    Try it
                  </button>
                </div>
              )}
            </div>
            <div 
              className="menu-item"
              onMouseEnter={() => setHoveredMenuItem('replace')}
              onMouseLeave={() => setHoveredMenuItem(null)}
            >
              <h3>Replace Object</h3>
              <p>Replace objects in your space</p>
              {hoveredMenuItem === 'replace' && (
                <div className="menu-item-preview">
                  <div className="before-after-container no-border">
                    <img 
                      src="https://iili.io/fMRWlIt.png" 
                      alt="Before" 
                      className={`before-after-image ${showBeforeAfter.replace ? 'show' : 'hide'}`}
                    />
                    <img 
                      src="https://iili.io/fMRWlIt.png" 
                      alt="After" 
                      className={`before-after-image ${!showBeforeAfter.replace ? 'show' : 'hide'}`}
                    />
                  </div>
                  <button 
                    className="try-it-btn"
                    onClick={() => {
                      if (!user) {
                        setShowGoogleSignInModal(true);
                      } else {
                        handleFeatureClick('replace');
                      }
                    }}
                  >
                    Try it
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Overlay when menu is open */}
        {menuOpen && (
          <div 
            className="side-panel-overlay"
            onClick={() => setMenuOpen(false)}
          />
        )}

        <div className="header-section">
          <img 
            src="https://iili.io/f0u0nyP.png" 
            alt="AI Home Designer Logo" 
            className="main-logo-img"
          />
          <h1 className="main-title">AI Home Designer</h1>
          <p className="description">
            Upload your room photo and a product image URL to see how any product looks in your space
          </p>
        </div>

        <form onSubmit={handleSubmit} className="generator-form">
          {/* Feature Title */}
          {selectedFeature && (
            <div className="form-group">
              <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>
                {selectedFeature === 'interior' && 'Interior Design'}
                {selectedFeature === 'exterior' && 'Exterior Design'}
                {selectedFeature === 'style' && 'Style Transfer'}
                {selectedFeature === 'paint' && 'Paint Room'}
                {selectedFeature === 'floor' && 'Floor Restyle'}
                {selectedFeature === 'replace' && 'Replace Object'}
              </h2>
            </div>
          )}

          {/* Step 1: Building Type (Exterior only) OR Room Type (Interior/others) */}
          {selectedFeature === 'exterior' ? (
            <div className="form-group form-step">
              <div className="step-indicator">
                <span className="step-number">1</span>
                <label className="form-label">Building Type (required)</label>
              </div>
              <div className="style-grid-container building-type-grid">
                {BUILDING_TYPES.map(type => (
                  <div
                    key={type}
                    className={`style-card ${buildingType === type ? 'selected' : ''}`}
                    onClick={() => setBuildingType(buildingType === type ? '' : type)}
                  >
                    <div className="style-card-title">
                      {type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </div>
                    <div className="style-card-image">
                      <img 
                        src={BUILDING_TYPE_IMAGES[type] || BUILDING_TYPE_IMAGES['house']} 
                        alt={type}
                        onError={(e) => {
                          e.target.src = BUILDING_TYPE_IMAGES['house'];
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : !selectedFeature || selectedFeature === 'interior' || selectedFeature === 'style' || selectedFeature === 'paint' || selectedFeature === 'floor' || selectedFeature === 'replace' ? (
            <div className="form-group form-step">
              <div className="step-indicator">
                <span className="step-number">1</span>
                <label className="form-label">Room Type (optional)</label>
              </div>
              <select
                value={roomType}
                onChange={(e) => setRoomType(e.target.value)}
                className="form-select"
              >
                <option value="">Select room type (optional)</option>
                {ROOM_TYPES.map(type => (
                  <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                ))}
              </select>
            </div>
          ) : null}

          {/* Step 2: Room/Exterior Image Upload OR Example Image Selection */}
          <div className="form-group form-step">
            <div className="step-indicator">
              <span className="step-number">2</span>
              <label className="form-label">
                {selectedFeature === 'exterior' ? 'Exterior Image' : 'Room Image'}
              </label>
            </div>
            <div 
              ref={dropZoneRef}
              className={`file-upload-zone ${isDragging ? 'dragging' : ''}`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
            <label htmlFor="room-image" className="file-label">
              {imagePreview ? (
                <div className="image-preview-box">
                  <img src={imagePreview} alt="Room preview" className="preview-img" />
                  <span className="change-text">Change</span>
                </div>
                ) : selectedExampleImage ? (
                  <div className="image-preview-box">
                    <img src={selectedExampleImage} alt="Example room" className="preview-img" />
                  <span className="change-text">Change</span>
                </div>
              ) : (
                <div className="file-upload-box">
                  <FiCamera className="upload-icon" />
                  <span>Room Photo</span>
                    <span className="format-hint">Accepted: {ACCEPTED_FORMATS_TEXT}</span>
                    <span className="drag-hint">Drag & drop or click to upload</span>
                </div>
              )}
            </label>
            <input
                ref={fileInputRef}
              id="room-image"
              type="file"
                accept={ACCEPTED_FORMATS.join(',')}
              onChange={handleFileChange}
              className="file-input"
            />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              {!imagePreview && !selectedExampleImage && (
                <div className="upload-options">
                  <button
                    type="button"
                    onClick={handleCameraClick}
                    className="upload-option-btn camera-btn"
                  >
                    <FiCamera className="camera-icon-white" />
                    Take Photo
                  </button>
                </div>
              )}
            </div>

            {/* Example Images Selection - Hide for exterior feature */}
            {selectedFeature !== 'exterior' && roomType && exampleImages[roomType] && !imagePreview && (
              <div className="example-images-section">
                <p className="example-images-label">Or choose from examples:</p>
                <div className="example-images-grid">
                  {exampleImages[roomType].slice(0, 6).map((url, idx) => (
                    <div
                      key={idx}
                      className={`example-image-card ${selectedExampleImage === url ? 'selected' : ''}`}
                      onClick={() => handleExampleImageSelect(url)}
                    >
                      <img src={url} alt={`Example ${idx + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Step 3: Feature-Specific Fields */}
          {selectedFeature === 'style' && (
            <div className="form-group form-step">
              <div className="step-indicator">
                <span className="step-number">3</span>
                <label className="form-label">Style Reference Image (required)</label>
              </div>
              <span className="format-hint">Upload or provide URL of the style image you want to apply</span>
              <input
                type="file"
                accept={ACCEPTED_FORMATS.join(',')}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) setStyleReferenceImage(file);
                }}
                className="file-input"
                style={{ marginTop: '10px' }}
              />
              <span style={{ margin: '10px 0', display: 'block', textAlign: 'center' }}>OR</span>
              <input
                type="text"
                placeholder="https://example.com/style-reference.jpg"
                value={styleReferenceUrl}
                onChange={(e) => setStyleReferenceUrl(e.target.value)}
                className="text-input"
              />
            </div>
          )}

          {selectedFeature === 'paint' && (
            <div className="form-group form-step">
              <div className="step-indicator">
                <span className="step-number">3</span>
                <label className="form-label">Paint Selection (required)</label>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <button
                  type="button"
                  onClick={() => {
                    // Get current room image source
                    const imgSrc = imagePreview || selectedExampleImage || (roomImage ? URL.createObjectURL(roomImage) : null);
                    if (imgSrc) {
                      setPaintCanvasImage(imgSrc);
                      setPaintCanvasMode('paint');
                      setShowPaintCanvas(true);
                    } else {
                      setError('Please upload a room image first');
                    }
                  }}
                  className="upload-option-btn camera-btn"
                  style={{ width: '100%', marginBottom: '10px' }}
                >
                  🎨 Paint Areas to Change
                </button>
                <p style={{ margin: '8px 0', fontSize: '0.9rem', color: '#666', textAlign: 'center' }}>OR</p>
                <label className="form-label" style={{ display: 'block', marginTop: '10px' }}>
                  Upload Paint Mask Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setPaintMask(file);
                  }}
                  className="file-input"
                  style={{ marginTop: '10px' }}
                />
                {paintMask && (
                  <p style={{ marginTop: '8px', color: '#30a6b1', fontSize: '0.9rem' }}>
                    ✓ Mask uploaded: {paintMask.name}
                  </p>
                )}
              </div>
              <div style={{ marginTop: '15px' }}>
                <label className="form-label">Paint Color (hex code)</label>
                <input
                  type="color"
                  value={paintColor}
                  onChange={(e) => setPaintColor(e.target.value)}
                  style={{ width: '100%', height: '50px', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  value={paintColor}
                  onChange={(e) => setPaintColor(e.target.value)}
                  placeholder="#FF5733"
                  className="text-input"
                  style={{ marginTop: '10px' }}
                />
              </div>
            </div>
          )}

          {selectedFeature === 'floor' && (
            <div className="form-group form-step">
              <div className="step-indicator">
                <span className="step-number">3</span>
                <label className="form-label">Floor Selection (required)</label>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '10px' }}>
                  Floor Selection (required)
                </label>
                <button
                  type="button"
                  onClick={() => {
                    // Get current room image source
                    const imgSrc = imagePreview || selectedExampleImage || (roomImage ? URL.createObjectURL(roomImage) : null);
                    if (imgSrc) {
                      setPaintCanvasImage(imgSrc);
                      setPaintCanvasMode('floor');
                      setShowPaintCanvas(true);
                    } else {
                      setError('Please upload a room image first');
                    }
                  }}
                  className="upload-option-btn camera-btn"
                  style={{ width: '100%', marginBottom: '10px' }}
                >
                  🎨 Paint Floor Areas
                </button>
                <p style={{ margin: '8px 0', fontSize: '0.9rem', color: '#666', textAlign: 'center' }}>OR</p>
                <label className="form-label" style={{ display: 'block', marginTop: '10px' }}>
                  Upload Floor Mask Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      console.log('Floor mask selected:', file.name);
                      setFloorMask(file);
                    }
                  }}
                  className="file-input"
                  style={{ marginTop: '10px' }}
                />
                {floorMask && (
                  <p style={{ marginTop: '8px', color: '#30a6b1', fontSize: '0.9rem' }}>
                    ✓ Mask uploaded: {floorMask.name}
                  </p>
                )}
              </div>
              <div style={{ marginTop: '15px' }}>
                <label className="form-label">Floor Material (required)</label>
                <select
                  value={floorMaterial}
                  onChange={(e) => setFloorMaterial(e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select floor material</option>
                  {FLOOR_MATERIALS.map(material => (
                    <option key={material} value={material}>
                      {material.charAt(0).toUpperCase() + material.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              {floorMaterial === 'custom' && (
                <div style={{ marginTop: '15px' }}>
                  <label className="form-label">Custom Floor Description (required)</label>
                  <textarea
                    value={customFloorDescription}
                    onChange={(e) => setCustomFloorDescription(e.target.value)}
                    placeholder="Describe your custom floor material..."
                    className="form-textarea"
                    rows="3"
                    required
                  />
                </div>
              )}
              <div style={{ marginTop: '15px' }}>
                <label className="form-label">Floor Color (optional, hex code)</label>
                <input
                  type="color"
                  value={floorColor || '#FFFFFF'}
                  onChange={(e) => setFloorColor(e.target.value)}
                  style={{ width: '100%', height: '50px', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  value={floorColor}
                  onChange={(e) => setFloorColor(e.target.value)}
                  placeholder="#FFFFFF (optional)"
                  className="text-input"
                  style={{ marginTop: '10px' }}
                />
              </div>
            </div>
          )}

          {selectedFeature === 'replace' && (
            <div className="form-group form-step">
              <div className="step-indicator">
                <span className="step-number">3</span>
                <label className="form-label">Object Selection (required)</label>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <button
                  type="button"
                  onClick={() => {
                    // Get current room image source
                    const imgSrc = imagePreview || selectedExampleImage || (roomImage ? URL.createObjectURL(roomImage) : null);
                    if (imgSrc) {
                      setPaintCanvasImage(imgSrc);
                      setPaintCanvasMode('replace');
                      setShowPaintCanvas(true);
                    } else {
                      setError('Please upload a room image first');
                    }
                  }}
                  className="upload-option-btn camera-btn"
                  style={{ width: '100%', marginBottom: '10px' }}
                >
                  🎨 Paint Object to Replace
                </button>
                <p style={{ margin: '8px 0', fontSize: '0.9rem', color: '#666', textAlign: 'center' }}>OR</p>
                <label className="form-label" style={{ display: 'block', marginTop: '10px' }}>
                  Upload Object Mask Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setObjectMask(file);
                  }}
                  className="file-input"
                  style={{ marginTop: '10px' }}
                />
                {objectMask && (
                  <p style={{ marginTop: '8px', color: '#30a6b1', fontSize: '0.9rem' }}>
                    ✓ Mask uploaded: {objectMask.name}
                  </p>
                )}
              </div>
              <div style={{ marginTop: '15px' }}>
                <label className="form-label">Replacement Object</label>
                <span className="format-hint">Upload image, provide URL, or describe the replacement object</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setReplacementObjectImage(file);
                  }}
                  className="file-input"
                  style={{ marginTop: '10px' }}
                />
                <span style={{ margin: '10px 0', display: 'block', textAlign: 'center' }}>OR</span>
                <input
                  type="text"
                  placeholder="https://example.com/replacement-object.jpg"
                  value={replacementObjectUrl}
                  onChange={(e) => setReplacementObjectUrl(e.target.value)}
                  className="text-input"
                />
                <span style={{ margin: '10px 0', display: 'block', textAlign: 'center' }}>OR</span>
                <textarea
                  value={replacementObjectDescription}
                  onChange={(e) => setReplacementObjectDescription(e.target.value)}
                  placeholder="Describe the replacement object (e.g., 'modern white coffee table')"
                  className="form-textarea"
                  rows="3"
                />
              </div>
            </div>
          )}

          {/* Product Image URL - only show if no feature selected (old product placement) */}
          {!selectedFeature && (
            <div className="form-group form-step">
              <div className="step-indicator">
                <span className="step-number">3</span>
                <label className="form-label">Product Image URL</label>
              </div>
              <span className="format-hint">Please provide the URL of the product image you want to see in your space.</span>
              <input
                type="text"
                style={{ marginTop: '10px' }}
                placeholder="https://example.com/product-image.jpg"
                value={productImageUrl}
                onChange={(e) => setProductImageUrl(e.target.value)}
                className="text-input"
              />
            </div>
          )}

          {/* Step 4: Style Selector (for interior, exterior, style transfer) */}
          {(selectedFeature === 'interior' || selectedFeature === 'exterior' || selectedFeature === 'style' || !selectedFeature) && (
            <div className="form-group form-step">
            <div className="step-indicator">
              <span className="step-number">4</span>
              <label className="form-label">Style (optional)</label>
            </div>
            <div className={`style-grid-container ${showAllStyles ? 'expanded' : ''}`}>
              {(showAllStyles ? STYLES : STYLES.slice(0, 8)).map(s => (
                <div
                  key={s}
                  className={`style-card ${style === s ? 'selected' : ''}`}
                  onClick={() => {
                    setStyle(s === style ? '' : s);
                    if (s !== 'custom') {
                      setCustomStylePrompt('');
                    }
                  }}
                >
                  <div className="style-card-title">{s.charAt(0).toUpperCase() + s.slice(1)}</div>
                  <div className="style-card-image">
                    <img 
                      src={STYLE_IMAGES[s] || STYLE_IMAGES['modern']} 
                      alt={s}
                      onError={(e) => {
                        e.target.src = STYLE_IMAGES['modern'];
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            {STYLES.length > 8 && (
              <button
                type="button"
                className="show-more-styles-btn"
                onClick={() => setShowAllStyles(!showAllStyles)}
              >
                {showAllStyles ? 'Show less' : 'Show more'}
              </button>
            )}

            {/* Custom Style Prompt */}
            {style === 'custom' && (
              <div className="form-group">
                <label className="form-label">Custom Style Description</label>
                <textarea
                  value={customStylePrompt}
                  onChange={(e) => setCustomStylePrompt(e.target.value)}
                  placeholder="Describe your custom style..."
                  className="form-textarea"
                  rows="3"
                />
              </div>
            )}
            </div>
          )}

          {/* Step 5: Color Palette Selector (for interior, exterior, style transfer) */}
          {(selectedFeature === 'interior' || selectedFeature === 'exterior' || selectedFeature === 'style' || !selectedFeature) && (
            <div className="form-group form-step">
              <div className="step-indicator">
                <span className="step-number">{selectedFeature ? (selectedFeature === 'style' ? '5' : '4') : '5'}</span>
                <label className="form-label">
                  Color Palette {selectedFeature === 'interior' || selectedFeature === 'exterior' ? '(required)' : '(optional)'}
                </label>
              </div>
            <div className="color-palette-container">
              <button
                type="button"
                className="palette-scroll-btn palette-scroll-left"
                onClick={() => {
                  if (colorPaletteGridRef.current) {
                    colorPaletteGridRef.current.scrollBy({ left: -150, behavior: 'smooth' });
                  }
                }}
                aria-label="Scroll left"
              >
                ‹
              </button>
              <div className="color-palette-grid" ref={colorPaletteGridRef}>
                {Object.keys(COLOR_PALETTES).map(palette => (
                  <div
                    key={palette}
                    className={`color-palette-card ${colorPalette === palette ? 'selected' : ''}`}
                    onClick={() => setColorPalette(colorPalette === palette ? '' : palette)}
                  >
                    <div className="color-palette-visual">
                      {palette === 'random' ? (
                        <div className="random-palette-gradient">
                          <svg width="100%" height="100%" viewBox="0 0 100 100" className="gift-icon">
                            <path
                              d="M50 20 L50 40 M40 25 L40 35 M60 25 L60 35 M35 30 L65 30 L65 50 L35 50 Z M40 50 L40 60 M60 50 L60 60 M40 60 L60 60 L60 70 L40 70 Z"
                              stroke="white"
                              strokeWidth="2"
                              fill="none"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                      ) : (
                        <div className="color-bars">
                          {COLOR_PALETTES[palette].map((color, idx) => (
                            <div
                              key={idx}
                              className="color-bar"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="color-palette-name">
                      {palette === 'random' ? 'Surprise Me' : palette.charAt(0).toUpperCase() + palette.slice(1)}
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="palette-scroll-btn palette-scroll-right"
                onClick={() => {
                  if (colorPaletteGridRef.current) {
                    colorPaletteGridRef.current.scrollBy({ left: 150, behavior: 'smooth' });
                  }
                }}
                aria-label="Scroll right"
              >
                ›
              </button>
            </div>
          </div>
          )}

          {/* Step 6: Reference Size (Optional) - only for old product placement */}
          {!selectedFeature && (
          <div className="form-group form-step reference-size-group">
            <div className="step-indicator">
              <span className="step-number">6</span>
              <label className="form-label">Reference size (optional)</label>
            </div>
            <div className="reference-size-row">
              <input
                type="number"
                min="0"
                step="0.1"
                value={referenceSizeValue}
                onChange={(e) => setReferenceSizeValue(e.target.value)}
                className="reference-size-input"
                placeholder="e.g. 84"
              />
              <div className="reference-size-units">
                <button
                  type="button"
                  className={`unit-toggle ${referenceSizeUnit === 'in' ? 'active' : ''}`}
                  onClick={() => setReferenceSizeUnit('in')}
                >
                  in
                </button>
                <button
                  type="button"
                  className={`unit-toggle ${referenceSizeUnit === 'cm' ? 'active' : ''}`}
                  onClick={() => setReferenceSizeUnit('cm')}
                >
                  cm
                </button>
              </div>
            </div>
            <p className="reference-size-hint">
              Width of your main sofa or the wall behind it. Helps estimate furniture sizes.
            </p>
          </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            disabled={loading || getSubmitDisabled()}
            className="generate-button"
            title={getSubmitDisabled() && !loading ? getDisabledReason() : ''}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                <GeneratingAnimation />
              </>
            ) : (
              <> Generate ✨</>
            )}
          </button>
        </form>
      </div>

      {/* Three-Step Process Section */}
      <div className="process-section">
        <div className="process-container">
          <h2 className="process-title">Visualize your dream space before committing.</h2>
          <div className="process-steps">
            {/* Step 1: Snap */}
            <div className="process-step">
              <div className="step-number">1</div>
              <h3 className="step-title">Snap</h3>
              <p className="step-description">Capture or upload any room.</p>
              <div className="step-visual snap-visual">
                <div className="phone-mockup">
                  <div className="phone-screen">
                    <img 
                      src={livingRoomImage} 
                      alt="Living room" 
                      className="room-preview-image"
                    />
                    <div className="upload-icon">📷</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Style */}
            <div className="process-step">
              <div className="step-number">2</div>
              <h3 className="step-title">Style</h3>
              <p className="step-description">Tap to recolor, refurnish or try 75+ curated looks (Modern → Victorian).</p>
              <div className="step-visual style-visual">
                <img 
                  src={mainScImage} 
                  alt="Style selector interface" 
                  className="style-screenshot"
                />
              </div>
            </div>

            {/* Step 3: Transform */}
            <div className="process-step">
              <div className="step-number">3</div>
              <h3 className="step-title">Transform</h3>
              <p className="step-description">Generate your redesigned space instantly.</p>
              <div className="step-visual transform-visual">
                <div className="generated-room-preview">
                  <img 
                    src={transformRoomImage} 
                    alt="Transformed room" 
                    className="transform-room-image"
                  />
                  <button className="generate-btn-overlay">
                    Generate
                    <span className="cursor-icon">👆</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Display Section */}
      <FeaturesDisplay 
        user={user}
        onFeatureClick={handleFeatureClick}
        onShowSignIn={() => {
          trackSignInModalOpened('try_it_button');
          setShowGoogleSignInModal(true);
        }}
      />

      {/* Paint Canvas Modal */}
      {showPaintCanvas && paintCanvasImage && (
        <PaintCanvas
          imageSrc={paintCanvasImage}
          mode={paintCanvasMode}
          onMaskGenerated={(blob) => {
            // Convert blob to File
            const file = new File([blob], `${paintCanvasMode}_mask.png`, { type: 'image/png' });
            if (paintCanvasMode === 'floor') {
              setFloorMask(file);
            } else if (paintCanvasMode === 'paint') {
              setPaintMask(file);
            } else if (paintCanvasMode === 'replace') {
              setObjectMask(file);
            }
            setShowPaintCanvas(false);
            setPaintCanvasImage(null);
          }}
          onClose={() => {
            setShowPaintCanvas(false);
            setPaintCanvasImage(null);
          }}
        />
      )}

      {/* Footer */}
      <footer className="main-footer">
        <div className="footer-content">
          <p>&copy; {new Date().getFullYear()} AI Home Designer. All rights reserved.</p>
          <div className="footer-links">
            <a href="mailto:stainstartups@gmail.com">Contact</a>
          </div>
        </div>
      </footer>

      {/* Camera Modal */}
      {showCamera && (
        <div className="camera-modal">
          <div className="camera-modal-content">
            <div className="camera-header">
              <h3>Take Photo</h3>
              <button onClick={closeCamera} className="camera-close-btn">
                <FiX />
              </button>
            </div>
            <div className="camera-preview">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="camera-video"
              />
            </div>
            <div className="camera-controls">
              <button
                onClick={capturePhoto}
                className="capture-btn"
              >
                <FiCamera />
                Capture Photo
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Loading Modal */}
      {loading && (
        <div className="loading-modal-overlay">
          <div className="loading-modal-content">
            <div className="loading-modal-spinner-container">
              <div className="loading-modal-spinner"></div>
            </div>
            <div className="loading-modal-text">
              <GeneratingAnimation />
            </div>
          </div>
        </div>
      )}

      {/* Subscription Paywall (RevenueCat) - Show for authenticated users */}
      {showSubscriptionModal && user && (
        <SubscriptionPaywall
          onClose={() => setShowSubscriptionModal(false)}
          onSuccess={(result) => {
            // Refresh subscription status after successful purchase
            if (subscription?.refresh) {
              subscription.refresh();
            }
            setShowSubscriptionModal(false);
            // Show success message
            alert('Welcome to Pro! Your subscription is now active.');
          }}
          user={user}
        />
      )}

      {/* Subscription Modal (Legacy) - Show for non-authenticated users */}
      {showSubscriptionModal && !user && (
        <div className="subscription-modal-overlay" onClick={() => setShowSubscriptionModal(false)}>
          <div className="subscription-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="subscription-modal-close-btn" 
              onClick={() => setShowSubscriptionModal(false)}
            >
              <FiX />
            </button>
            <div className="subscription-modal-body">
              <div className="subscription-icon">
                <FiHeart />
              </div>
              <h2>Unlock Unlimited Generations</h2>
              <p className="subscription-subtitle">
                {subscription.isPro 
                  ? "You've reached your generation limit."
                  : subscription.generationsRemaining === 0
                  ? "You've used your 2 free generations. Upgrade to Pro for unlimited access!"
                  : "Upgrade to Pro for unlimited generations!"}
              </p>
              
              <div className="subscription-benefits">
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>Unlimited generations</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>Faster processing</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>Complete package of all features</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>New floor design options</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>Painting visualization tools</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>And much more!</span>
                </div>
              </div>

              {!user ? (
                <>
                  <button 
                    className="subscription-cta-btn"
                    onClick={() => {
                      setShowSubscriptionModal(false);
                      handleGoogleSignIn();
                    }}
                  >
                    Sign In to Continue
                  </button>
                  <p className="subscription-note">
                    Sign in to unlock unlimited generations and access all premium features
                  </p>
                </>
              ) : (
                <>
                  <button 
                    className="subscription-cta-btn"
                    onClick={() => {
                      setShowSubscriptionModal(false);
                      // Show RevenueCat paywall instead
                      // This will be handled by replacing the subscription modal with paywall
                    }}
                  >
                    Upgrade to Pro
                  </button>
                  <p className="subscription-note">
                    Upgrade to Pro for unlimited generations, 4K exports, and more!
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Google Sign-In Modal */}
      {showGoogleSignInModal && (
        <div className="google-signin-modal-overlay" onClick={() => setShowGoogleSignInModal(false)}>
          <div className="google-signin-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close-btn"
              onClick={() => setShowGoogleSignInModal(false)}
            >
              <FiX />
            </button>
            <div className="google-signin-modal-body">
              <div className="save-icon-large">
                <FiHeart />
              </div>
              <h3>Save Your Design</h3>
              <p>Sign in to save and access your designs anytime</p>
              <button className="google-signin-btn" onClick={handleGoogleSignIn}>
                <FaGoogle />
                <span>Continue with Google</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Image Confirmation Modal */}
      {showUnsavedConfirmModal && (
        <div className="subscription-modal-overlay" onClick={handleCancelReset}>
          <div className="subscription-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="subscription-modal-close-btn" 
              onClick={handleCancelReset}
            >
              <FiX />
            </button>
            <div className="subscription-modal-body">
              <div className="subscription-icon">
                <FiHeart />
              </div>
              <h2>Are you sure you don't want to save this image?</h2>
              <p className="subscription-subtitle">
                This image hasn't been saved yet. If you close now, you'll lose access to it.
              </p>
              
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'center' }}>
                <button 
                  className="subscription-cta-btn"
                  onClick={async () => {
                    if (user) {
                      // Save first, then reset
                      if (!isSaved && generatedImage) {
                        try {
                          if (canSave(subscription)) {
                            const result = await saveImageToFirebase(
                              generatedImage,
                              user.uid,
                              currentDesignId
                            );
                            setIsSaved(true);
                            setSavedImageDocId(result.docId);
                            setSavedImageUrl(result.downloadURL);
                            setCurrentDesignId(result.designId);
                            trackImageSaved(true);
                          } else {
                            setShowSubscriptionModal(true);
                            setShowUnsavedConfirmModal(false);
                            return;
                          }
                        } catch (error) {
                          console.error('Error saving before reset:', error);
                          setError('Failed to save image');
                          return;
                        }
                      }
                    } else {
                      // Not signed in, show sign-in modal
                      setShowUnsavedConfirmModal(false);
                      setShowGoogleSignInModal(true);
                      return;
                    }
                    handleConfirmReset();
                  }}
                  style={{ background: '#30a6b1', color: 'white' }}
                >
                  Yes, Save & Close
                </button>
                <button 
                  className="subscription-cta-btn"
                  onClick={handleConfirmReset}
                  style={{ background: '#e0e0e0', color: '#666' }}
                >
                  No, Close Without Saving
                </button>
                <button 
                  className="subscription-cta-btn"
                  onClick={handleCancelReset}
                  style={{ background: '#f5f5f5', color: '#666', border: '1px solid #ddd' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Landing;

